import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Snackbar } from '@mui/material';
import { Mail, MessageCircle, Calendar } from 'lucide-react';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 100px 20px;
  background: #000000;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
`;

const Header = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 48px;
  font-weight: 800;
  color: #FFFFFF;
  margin: 0;
`;

const Desc = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.6);
  max-width: 600px;
  line-height: 1.6;
  margin: 0;
`;

const ContentGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 40px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ContactCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 30px;
  display: flex;
  align-items: center;
  gap: 24px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateX(10px);
  }
`;

const IconWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ color }) => color || '#FFF'};
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitle = styled.h4`
  font-size: 18px;
  color: #FFF;
  margin: 0;
`;

const CardText = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

const RightSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: 40px;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 14px 20px;
  color: #FFF;
  font-size: 15px;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    border-color: #854CE6;
    background: rgba(0, 0, 0, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const TextArea = styled.textarea`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 14px 20px;
  color: #FFF;
  font-size: 15px;
  outline: none;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;

  &:focus {
    border-color: #854CE6;
    background: rgba(0, 0, 0, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const SubmitButton = styled.button`
  background: #FFFFFF;
  color: #000000;
  border: none;
  border-radius: 8px;
  padding: 14px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #F0F0F0;
    transform: translateY(-2px);
  }
`;

const Contact = () => {
  const [open, setOpen] = useState(false);
  const form = useRef();

  const handleSubmit = (e) => {
    e.preventDefault();
    fetch('https://getform.io/f/rbeqdjlb', {
      method: 'POST',
      body: new FormData(form.current),
    })
    .then((response) => {
      if (response.ok) {
        setOpen(true);
        form.current.reset();
      }
    })
    .catch((error) => console.error('Form submission error:', error));
  };

  return (
    <Container id="contact">
      <Wrapper>
        <Header
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Title>Let's Connect</Title>
          <Desc>
            Ready to discuss your next product idea or explore collaboration opportunities? I'd love to hear from you.
          </Desc>
        </Header>

        <ContentGrid>
          <LeftSection
            as={motion.div}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ContactCard onClick={() => window.open('mailto:kumarrakesh51530@gmail.com')}>
              <IconWrapper color="#FF4B4B">
                <Mail size={24} />
              </IconWrapper>
              <CardContent>
                <CardTitle>Email</CardTitle>
                <CardText>Hit me an email</CardText>
              </CardContent>
            </ContactCard>

            <ContactCard onClick={() => window.open('https://wa.me/7889440384')}>
               <IconWrapper color="#25D366">
                <MessageCircle size={24} />
              </IconWrapper>
              <CardContent>
                <CardTitle>WhatsApp</CardTitle>
                <CardText>Let's chat directly</CardText>
              </CardContent>
            </ContactCard>

            <ContactCard>
              <IconWrapper color="#3B82F6">
                <Calendar size={24} />
              </IconWrapper>
              <CardContent>
                <CardTitle>Book a Call</CardTitle>
                <CardText>Book a 30-minute product strategy session</CardText>
              </CardContent>
            </ContactCard>
          </LeftSection>

          <RightSection
            as={motion.div}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ContactForm ref={form} onSubmit={handleSubmit}>
              <Input type="text" placeholder="Name *" name="from_name" required />
              <Input type="email" placeholder="Email address *" name="from_email" required />
              <Input type="text" placeholder="Website/Social (optional)" name="website" />
              <Input type="text" placeholder="Subject (optional)" name="subject" />
              <TextArea placeholder="Your message *" name="message" required />
              <SubmitButton type="submit">Send Message</SubmitButton>
            </ContactForm>
          </RightSection>
        </ContentGrid>
      </Wrapper>
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={() => setOpen(false)}
        message="Message sent successfully!"
      />
    </Container>
  );
};

export default Contact;
